import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CollectDepositModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (grossAmount: number, notes?: string) => Promise<void>;
	isSubmitting: boolean;
	cardKey?: string | null;
}

export const CollectDepositModal = ({
	open,
	onOpenChange,
	onConfirm,
	isSubmitting,
	cardKey,
}: CollectDepositModalProps) => {
	const { t: tC } = useTranslation('contracts');
	const [amount, setAmount] = useState('');
	const [notes, setNotes] = useState('');

	const parsedAmount = Number(amount);
	const isValid = amount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount > 0;

	const handleSubmit = async () => {
		if (!isValid) return;
		await onConfirm(parsedAmount, notes || undefined);
		setAmount('');
		setNotes('');
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setAmount('');
			setNotes('');
		}
		onOpenChange(nextOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent size="sm">
				<DialogHeader>
					<DialogTitle>{tC('guarantees.collectDepositTitle')}</DialogTitle>
					<DialogDescription>
						{tC('guarantees.collectDepositDescription', { cardKey: cardKey ?? '—' })}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label htmlFor="deposit-amount">
							{tC('guarantees.amount')}
						</Label>
						<Input
							id="deposit-amount"
							type="number"
							min="0.01"
							step="0.01"
							placeholder="0.00"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="deposit-notes">
							{tC('guarantees.notes')}
						</Label>
						<Textarea
							id="deposit-notes"
							placeholder={tC('guarantees.notesPlaceholder')}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							rows={2}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={isSubmitting}
					>
						{tC('guarantees.cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!isValid || isSubmitting}
					>
						{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{tC('guarantees.collectDeposit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
